import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { SignInResponseModel } from '@core/auth/models/user-login.model';
import { AuthService } from '@core/services/auth.service';
import {
  Receipt,
  ReceiptRequest,
  ReceiptResponse,
} from '@features/payments/interfaces/payment.interface';
import { SearchFilterComponent } from '@shared/components/search-filter/search-filter.component';
import { FilterDescription } from '@shared/interfaces/filter-description.interface';
import { CommonService } from '@shared/services/common.service';
import { PaymentHistoryCardComponent } from '../payment-history-card/payment-history-card.component';

@Component({
  selector: 'app-payment-history',
  imports: [PaymentHistoryCardComponent, SearchFilterComponent, DatePipe],
  templateUrl: './payment-history.component.html',
  styleUrl: './payment-history.component.scss',
})
export class PaymentHistoryComponent implements OnInit {
  userInfo: SignInResponseModel;
  receipts: ReceiptResponse | null = null;
  filteredReceipts: ReceiptResponse | null = null;
  startDate = new Date();
  endDate = new Date();
  filteredByDate = false;

  currentPage = 1;
  itemsPerPage = 4;
  totalPages = 1;

  filters: FilterDescription[] = [
    {
      label: 'Fecha del recibo',
      value: 'date',
      img: 'assets/icons/calendar-icon.svg',
      isAdvance: false,
      isDateRange: true,
    },
    {
      label: 'Más reciente a más antiguo',
      value: 'date-desc',
      img: 'assets/icons/sort-desc.svg',
      isAdvance: false,
      isDateRange: false,
    },
    {
      label: 'Más antiguo a más reciente',
      value: 'date-asc',
      img: 'assets/icons/sort-asc.svg',
      isAdvance: false,
      isDateRange: false,
    },
    {
      label: 'Limpiar Filtros',
      value: 'clear',
      img: '',
      isAdvance: false,
      isDateRange: false,
    },
  ];

  constructor(
    private readonly _commonService: CommonService,
    private readonly _authService: AuthService,
  ) {
    this.userInfo = this._authService.getUserInfo()!;
  }

  ngOnInit(): void {
    this.getPreviousYearReceipts();
  }

  /**
   * Filters and sorts receipts based on user input.
   */
  filterLogic = (filters: {
    search: string;
    type: string;
    selectedDate: Date | null;
    startDate: Date | null;
    endDate: Date | null;
  }) => {
    if (filters.type === 'clear' || filters.type === '') {
      filters.startDate = null;
      filters.endDate = null;
    }

    if (!this.receipts?.data?.receipts) return;

    const searchValue = filters.search?.toLowerCase() || '';

    if (filters.startDate && filters.endDate) {
      this.startDate = filters.startDate;
      this.endDate = filters.endDate;
      this.filteredByDate = true;
      const request: ReceiptRequest = {
        clientcode: this.userInfo.clientCode,
        begindate: filters.startDate.toISOString().split('T')[0],
        enddate: filters.endDate.toISOString().split('T')[0],
      };

      this._commonService.getPaymentReceipts(request).subscribe({
        next: (response) => {
          this.receipts = response;
          if (this.receipts) {
            this.receipts.data.receipts = this.aggregateReceipts(
              this.receipts.data.receipts,
            );

            this.filteredReceipts = { ...this.receipts };
            this.filteredReceipts.data.receipts.sort(
              (a, b) =>
                new Date(b.dateReceipts || 0).getTime() -
                new Date(a.dateReceipts || 0).getTime(),
            );
          } else {
            this.filteredReceipts = null;
          }
          this.resetPagination();
        },
        error: () => {
          return;
        },
      });

      return;
    }

    // Filter and sort based on the selected filter
    switch (filters.type) {
      case 'date-desc':
        this.filteredReceipts?.data.receipts.sort(
          (a, b) =>
            new Date(b.dateReceipts || 0).getTime() -
            new Date(a.dateReceipts || 0).getTime(),
        );
        break;

      case 'date-asc':
        this.filteredReceipts?.data.receipts.sort(
          (a, b) =>
            new Date(a.dateReceipts || 0).getTime() -
            new Date(b.dateReceipts || 0).getTime(),
        );
        break;

      case 'date':
        break;

      case 'text':
        break;

      case 'clear':
      default:
        this.getPreviousYearReceipts();
        this.filteredByDate = false;
        break;
    }

    if (this.filteredReceipts && filters.type === 'text') {
      const filteredData = searchValue
        ? this.receipts.data.receipts.filter((receipt) =>
            receipt.receipt.toString().toLowerCase().includes(searchValue),
          )
        : [...this.receipts.data.receipts];

      this.filteredReceipts = {
        ...this.receipts,
        data: { receipts: filteredData },
      };
    }

    this.resetPagination();
  };

  /**
   * Resets pagination when filtered data changes.
   */
  public resetPagination() {
    this.currentPage = 1;
    this.updatePagination();
  }

  /**
   * Updates pagination metadata based on filtered data.
   */
  private updatePagination() {
    if (this.filteredReceipts?.data?.receipts) {
      this.totalPages = Math.ceil(
        this.filteredReceipts.data.receipts.length / this.itemsPerPage,
      );
    } else {
      this.totalPages = 1;
    }
  }

  /**
   * Returns the paginated items for the current page.
   */
  getPaginatedItems() {
    if (!this.filteredReceipts?.data?.receipts) return [];

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    return this.filteredReceipts.data.receipts.slice(startIndex, endIndex);
  }

  /**
   * Utility to format a date as 'YYYY-MM-DD'.
   */
  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Handles page navigation.
   */
  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  /**
   * Groups receipts by the 'receipt' field and aggregates their totalPaid and count.
   */
  private aggregateReceipts(
    receipts: Receipt[],
  ): (Receipt & { numberOfReceipts: number })[] {
    const groups = receipts.reduce(
      (acc, receipt) => {
        const key = receipt.receipt;
        if (!acc[key]) {
          acc[key] = { ...receipt, numberOfReceipts: 0 };
        }

        acc[key].numberOfReceipts += 1;
        return acc;
      },
      {} as Record<number, Receipt & { numberOfReceipts: number }>,
    );
    return Object.values(groups);
  }

  /**
   * Retrieves receipts for the previous year (using mockData for now) and aggregates duplicates.
   */
  getPreviousYearReceipts(): void {
    const today = new Date();
    const enddate = new Date(today);
    enddate.setDate(today.getDate());

    const begindate = new Date(enddate);
    begindate.setFullYear(enddate.getFullYear() - 1);

    const request: ReceiptRequest = {
      clientcode: this.userInfo.clientCode,
      begindate: this.formatDate(begindate),
      enddate: this.formatDate(enddate),
    };

    this._commonService.getPaymentReceipts(request).subscribe({
      next: (response) => {
        this.receipts = response;
        if (this.receipts) {
          const aggregated = this.aggregateReceipts(
            this.receipts.data.receipts,
          );
          aggregated.sort(
            (a, b) =>
              new Date(b.dateReceipts || 0).getTime() -
              new Date(a.dateReceipts || 0).getTime(),
          );
          this.receipts.data.receipts = aggregated;
          this.filteredReceipts = { ...this.receipts };
          this.resetPagination();
        }
      },
      error: () => {
        return;
      },
    });
  }
}
